import React, { useState, useContext } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import {
  Button, Container, Paper, Typography, Alert, CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { issueFields } from "../../../AppStore/reducers/defaultStates";
import { AuthContext } from "../../../AppContext/AuthContext";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import { db } from "../../../Config/firebase";
import { v4 as uuid } from "uuid";
import {
  query,
  getDocs,
  setDoc,
  doc,
  where,
  collection,
  serverTimestamp,
  limit,
  orderBy,
} from "firebase/firestore";
import Basic from "./Basic";
import Ranking from "./Ranking";
import Classify from "./Classify";
import FormAlert from "../../../Pay/FormAlert";
import Billing from "./Billing";
import { ArrowBack, RocketLaunch } from "@mui/icons-material";

const steps = ["Project Details", "Budget & Priority"];

export default function FormStepper() {
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm(issueFields);
  const navigate = useNavigate();
  const { userInfo } = useContext(AuthContext);
  const { refreshProjects } = useContext(ProjectContext);
  const hfControl = { register, errors };

  const projectType = watch("projectType");
  const showBudget = projectType && projectType !== "no-cost";

  const onIssueUpdate = async (data) => {
    setSubmitting(true);
    setSubmitError(null);
    Object.keys(data).forEach((key) => {
      if (!data[key]) {
        data[key] = null;
      }
    });
    data.communityCode = userInfo.communityCode;
    data.creator = userInfo.uid;
    const selfId = uuid();
    data.id = selfId;
    data.timestamp = serverTimestamp();
    data.active = true;
    data.status = "open";
    data.progress = 0;
    try {
      let maxNumber = 999;
      try {
        const docSnap = await getDocs(
          query(
            collection(db, "projects"),
            where("communityCode", "==", userInfo.communityCode),
            orderBy("projectNumber", "desc"),
            limit(1)
          )
        );
        docSnap.forEach((each) => {
          if (each.data()?.projectNumber) {
            maxNumber = each.data().projectNumber;
          }
        });
      } catch (indexErr) {
        console.warn("orderBy query failed (index may be missing), falling back:", indexErr);
        const allSnap = await getDocs(
          query(
            collection(db, "projects"),
            where("communityCode", "==", userInfo.communityCode)
          )
        );
        allSnap.forEach((each) => {
          const num = each.data()?.projectNumber;
          if (num && num > maxNumber) maxNumber = num;
        });
      }
      data.projectNumber = maxNumber + 1;
      await setDoc(doc(db, "projects", selfId), data);
      refreshProjects();
      setActiveStep(steps.length); // show success
    } catch (err) {
      console.error("Project creation failed:", err);
      setSubmitError("Failed to create project. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = (data) => {
    if (activeStep === steps.length - 1) {
      onIssueUpdate(data);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <Basic control={control} hfControl={hfControl} />
            <Box sx={{ mt: 4 }}>
              <Classify
                control={control}
                hfControl={hfControl}
                watch={watch}
                setValue={setValue}
              />
            </Box>
          </>
        );
      case 1:
        return (
          <>
            {showBudget && (
              <Box sx={{ mb: 4 }}>
                <Billing control={control} hfControl={hfControl} />
              </Box>
            )}
            <Ranking
              control={control}
              hfControl={hfControl}
              watch={watch}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Gradient Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <RocketLaunch sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              New Development Project
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Step {activeStep + 1} of {steps.length} — {steps[activeStep]}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBack />}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,.4)" }}
          onClick={() => navigate("/works")}
        >
          Back
        </Button>
      </Paper>

      {/* Stepper */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid #e0e0e0" }}
      >
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {activeStep === steps.length ? (
        <FormAlert
          message="Project has been created successfully"
          description=""
          route="/works"
          buttonText="View all projects"
        />
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid #e0e0e0", minHeight: 400 }}
          >
            {renderStep()}
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
              onClick={handleSubmit(handleNext)}
              sx={{
                background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
                px: 4,
              }}
            >
              {activeStep === steps.length - 1
                ? submitting
                  ? "Creating…"
                  : "Create Project"
                : "Next Step"}
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
}
